"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { Button, Modal, ModalHeader, ModalBody, FormGroup, Label, Input, Spinner, FormFeedback } from "reactstrap"
import Swal from "sweetalert2"
import type { Identification } from "./types"
import IdentificationService from "../../../Service/IdentificationService"
import { toast } from "react-toastify"

interface Props {
  isOpen: boolean
  toggle: () => void
  onSuccess: () => void
  initialData: Identification | null
}

const IdentificationModal = ({ isOpen, toggle, onSuccess, initialData }: Props) => {
  const [form, setForm] = useState<Omit<Identification, "id">>({
    name: "",
    length: 0,
    requires_country: false,
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<{
    name?: string
    length?: string
  }>({})

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name,
        length: initialData.length,
        requires_country: initialData.requires_country,
      })
    } else {
      setForm({ name: "", length: 0, requires_country: false })
    }
    // Clear errors when modal opens/closes or data changes
    setErrors({})
  }, [initialData, isOpen])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    
    let processedValue: any = type === "checkbox" ? checked : value
    
    // Convert length to number
    if (name === "length") {
      processedValue = value === "" ? 0 : Number(value)
    }

    setForm({
      ...form,
      [name]: processedValue,
    })

    // Clear error for this field when user starts typing/changing
    if (errors[name as keyof typeof errors]) {
      setErrors({
        ...errors,
        [name]: undefined,
      })
    }
  }

  const validateForm = (): boolean => {
    const newErrors: {
      name?: string
      length?: string
    } = {}

    // Name validation
    if (!form.name.trim()) {
      newErrors.name = "Name is required"
    } else if (form.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters long"
    } else if (form.name.trim().length > 100) {
      newErrors.name = "Name must not exceed 100 characters"
    } else if (!/^[a-zA-Z0-9\s\-_&().,]+$/.test(form.name.trim())) {
      newErrors.name = "Name contains invalid characters"
    }

    // Length validation
    if (form.length < 0) {
      newErrors.length = "Length cannot be negative"
    } else if (form.length > 50) {
      newErrors.length = "Length cannot exceed 50 characters"
    } else if (!Number.isInteger(form.length)) {
      newErrors.length = "Length must be a whole number"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const resetForm = () => {
    setForm({ name: "", length: 0, requires_country: false })
    setErrors({})
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fix the validation errors")
      return
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to ${initialData ? "update" : "create"} this Identification?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${initialData ? "update" : "create"} it!`,
      cancelButtonText: "Cancel",
    })

    if (!result.isConfirmed) {
      return
    }

    try {
      setLoading(true)

      const formData = {
        name: form.name.trim(),
        length: Number(form.length),
        requires_country: form.requires_country,
      }

      if (initialData) {
        await IdentificationService.update(initialData.id, formData)

        await Swal.fire({
          title: "Updated!",
          text: "Identification has been updated successfully.",
          icon: "success",
          confirmButtonColor: "#28a745",
        })
      } else {
        await IdentificationService.create(formData)

        await Swal.fire({
          title: "Created!",
          text: "Identification has been created successfully.",
          icon: "success",
          confirmButtonColor: "#28a745",
        })

        // Reset form after successful creation
        resetForm()
      }

      onSuccess() // refetch
      toggle()
    } catch (error: any) {
      console.error("Identification operation error:", error)

      let errorMessage = "Something went wrong. Please try again."

      // Handle specific error cases
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.data?.name) {
        errorMessage = Array.isArray(error.response.data.name) ? error.response.data.name[0] : error.response.data.name
      } else if (error.response?.data?.length) {
        errorMessage = Array.isArray(error.response.data.length) ? error.response.data.length[0] : error.response.data.length
      } else if (error.response?.data?.non_field_errors) {
        errorMessage = Array.isArray(error.response.data.non_field_errors) 
          ? error.response.data.non_field_errors[0] 
          : error.response.data.non_field_errors
      }

      await Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        confirmButtonColor: "#dc3545",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleModalToggle = () => {
    if (!initialData) {
      resetForm()
    }
    toggle()
  }

  return (
    <Modal isOpen={isOpen} toggle={handleModalToggle}>
      <ModalHeader toggle={handleModalToggle}>
        {initialData ? "Edit" : "Add"} Identification
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="name">
            Name <span className="text-danger">*</span>
          </Label>
          <Input
            name="name"
            value={form.name}
            onChange={handleChange}
            invalid={!!errors.name}
            placeholder="Enter identification name"
            maxLength={100}
          />
          {errors.name && <FormFeedback>{errors.name}</FormFeedback>}
          <small className="text-muted">{form.name.length}/100 characters</small>
        </FormGroup>

        <FormGroup>
          <Label for="length">
            Length <span className="text-danger">*</span>
          </Label>
          <Input
            type="number"
            name="length"
            value={form.length}
            onChange={handleChange}
            invalid={!!errors.length}
            min="0"
            max="50"
            placeholder="Enter required length"
          />
          {errors.length && <FormFeedback>{errors.length}</FormFeedback>}
          <small className="text-muted">Maximum character length for this identification type</small>
        </FormGroup>

        <FormGroup check>
          <Label check>
            <Input
              type="checkbox"
              name="requires_country"
              checked={form.requires_country}
              onChange={handleChange}
            />
            <span className="ms-2">Requires Country</span>
          </Label>
          <small className="text-muted d-block mt-1">
            {form.requires_country 
              ? "This identification type requires country information" 
              : "Country information is optional for this type"
            }
          </small>
        </FormGroup>

        <div className="text-end mt-4">
          <Button color="secondary" onClick={handleModalToggle} className="me-2" disabled={loading}>
            Cancel
          </Button>
          <Button 
            color="primary" 
            onClick={handleSubmit} 
            disabled={loading || !form.name.trim()}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-1" />
                {initialData ? "Updating..." : "Creating..."}
              </>
            ) : initialData ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default IdentificationModal